/** Reports → Performance: utilisation, visits and points per consultant for any date range. */
import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Panel, ShowAllButton } from "./ui";
import { initials } from "./ConsultantsScreen";
import { useActiveConsultants, useDiary, useTeamFilter, useTeamOf, useVisitsByConsultant, roomForSecond, scoreConsultantPeriod } from "./data";
import type { DiaryDay, SfVisit } from "./data";
import { fmtPoints, monthlyVisitTarget, fieldDaysPerMonth } from "./points";
import { usePointsConfig } from "./pointsConfig";
import { weekdaysBetween } from "./reportRange";
import type { ReportRange } from "./reportRange";

/** A visit stays Booked until its report is written, so Booked counts as delivered; Closed never counts. */
const BOOKED_STATUSES = new Set(["Booked", "Completed"]);

/** Targets are set per month; spread over the year's weekdays so any range scales the same way. */
const WEEKDAYS_PER_YEAR = 260;

export interface DayBreakdown {
    weekdays: number;
    leave: number;
    sickness: number;
    available: number;
    booked: number;
    admin: number;
    gap: number;
}

const EMPTY: DayBreakdown = { weekdays: 0, leave: 0, sickness: 0, available: 0, booked: 0, admin: 0, gap: 0 };

/** Leave and sickness leave the denominator; a visit outranks admin on the same day. */
export const breakdownFor = (days: string[], bookedDays: Set<string>, diary: Map<string, DiaryDay["status"]> | undefined): DayBreakdown => {
    const b = { ...EMPTY, weekdays: days.length };
    for (const d of days) {
        const s = diary?.get(d);
        if (s === "H") b.leave++;
        else if (s === "S") b.sickness++;
        else if (bookedDays.has(d)) b.booked++;
        else if (s === "A") b.admin++;
        else b.gap++;
    }
    b.available = b.weekdays - b.leave - b.sickness;
    return b;
};

const addB = (a: DayBreakdown, b: DayBreakdown): DayBreakdown => ({
    weekdays: a.weekdays + b.weekdays,
    leave: a.leave + b.leave,
    sickness: a.sickness + b.sickness,
    available: a.available + b.available,
    booked: a.booked + b.booked,
    admin: a.admin + b.admin,
    gap: a.gap + b.gap,
});

const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : "—");

export const utilisation = (b: DayBreakdown) => ({
    all: pct(b.booked + b.admin, b.available),
    exAdmin: pct(b.booked, b.available - b.admin),
    visits: pct(b.booked, b.available),
    ratio: b.admin > 0 ? `${(b.booked / b.admin).toFixed(2)} : 1` : "—",
});

interface Row {
    email: string;
    name: string;
    team: string;
    b: DayBreakdown;
    visits: number;
    visitsGoal: number;
    physical: number;
    remote: number;
    unflagged: number;
    /** Nothing at all in the range: no visits, leave, sickness or admin. */
    empty: boolean;
    missedDoubles: number;
    points: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

type Col = { label: string; tip?: string; get: (r: Row) => string | number; total?: (rows: Row[], b: DayBreakdown) => string | number; strong?: boolean; points?: boolean; colour?: (r: Row) => string | undefined };

/** Steve's bands for a 15-a-month consultant (15+ green, 14 amber, 13 or under red), applied as a share of each target. */
const visitColour = (r: Row) => {
    if (!r.visitsGoal) return undefined;
    const share = r.visits / r.visitsGoal;
    return share >= 1 ? T.green : share >= 14 / 15 ? T.amber : T.red;
};

export type SortState = { key: string; dir: 1 | -1 };

const sortValue = (r: Row, c?: Col) => {
    if (!c) return r.name.toLowerCase();
    const n = parseFloat(String(c.get(r)));
    return Number.isFinite(n) ? n : -Infinity;
};

const sum = (rows: Row[], k: keyof Row) => rows.reduce((t, r) => t + (r[k] as number), 0);

const COLUMNS: Col[] = [
    { label: "Leave", tip: "Holiday, including bank holidays — they are recorded as holiday", get: (r) => r.b.leave, total: (_, b) => b.leave },
    { label: "Sick", get: (r) => r.b.sickness, total: (_, b) => b.sickness },
    { label: "Available", tip: "Weekdays less leave and sickness", get: (r) => r.b.available, total: (_, b) => b.available },
    { label: "Booked", tip: "Days with a visit that is Booked or Completed", get: (r) => r.b.booked, total: (_, b) => b.booked },
    { label: "Admin", tip: "From Outlook calendar entries categorised Admin", get: (r) => r.b.admin, total: (_, b) => b.admin },
    { label: "Gap", tip: "Available, with no visit or admin", get: (r) => r.b.gap, total: (_, b) => b.gap },
    { label: "Util · all", tip: "(Booked + admin) ÷ available", get: (r) => utilisation(r.b).all, total: (_, b) => utilisation(b).all, strong: true },
    { label: "Util · excl admin", tip: "Booked ÷ (available − admin)", get: (r) => utilisation(r.b).exAdmin, total: (_, b) => utilisation(b).exAdmin, strong: true },
    { label: "Visit util", tip: "Booked ÷ available", get: (r) => utilisation(r.b).visits, total: (_, b) => utilisation(b).visits, strong: true },
    { label: "Booked to admin", tip: "Visit days for every admin day", get: (r) => utilisation(r.b).ratio, total: (_, b) => utilisation(b).ratio },
    { label: "Visits", tip: "Booked or Completed visits in the range; green on target, amber just under, red below", get: (r) => r.visits, total: (rows) => sum(rows, "visits"), colour: visitColour },
    { label: "Physical", tip: "On-site visits", get: (r) => r.physical, total: (rows) => sum(rows, "physical") },
    { label: "Remote", get: (r) => r.remote, total: (rows) => sum(rows, "remote") },
    { label: "Missed dbls", tip: "Single-visit days short enough to fit a second", get: (r) => r.missedDoubles, total: (rows) => sum(rows, "missedDoubles"), points: true },
    { label: "Points", tip: "Recognition points for the range", get: (r) => fmtPoints(r.points), total: (rows) => fmtPoints(round1(sum(rows, "points"))), points: true },
];

const Figure = ({ value, label, tip, color }: { value: string; label: string; tip: string; color?: string }) => (
    <Box title={tip} sx={{ background: T.rowTint, border: `1px solid ${T.borderSoft}`, borderRadius: "8px", padding: "10px 11px" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 800, color: color || T.ink2, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
        <Typography sx={{ fontSize: "10.5px", color: T.muted, lineHeight: 1.3 }}>{label}</Typography>
    </Box>
);

const ConsultantTable = ({ rows, all, total, totalLabel, cols, onOpen, sort, onSort }: { rows: Row[]; all: Row[]; total: DayBreakdown; totalLabel: string; cols: Col[]; onOpen?: (r: Row) => void; sort: SortState; onSort: (key: string) => void }) => {
    const grid = `minmax(190px, 1.8fr) repeat(${cols.length}, minmax(60px, 1fr))`;
    const rowSx = { display: "grid", gridTemplateColumns: grid, gap: "8px", alignItems: "center", padding: "9px 10px", "& > *:not(:first-child)": { textAlign: "right" } };
    const numSx = (strong?: boolean, muted?: boolean) => ({ fontSize: "13px", fontWeight: strong ? 700 : 400, color: muted ? T.muted : strong ? T.purple : T.ink2, fontVariantNumeric: "tabular-nums" });
    return (
        <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: `${190 + cols.length * 68}px` }}>
                <Box sx={{ ...rowSx, background: T.rowTint, borderRadius: "8px", padding: "8px 10px" }}>
                    {["Consultant", ...cols.map((c) => c.label)].map((h, i) => (
                        <Typography
                            key={h}
                            component="button"
                            onClick={() => onSort(h)}
                            title={`${i ? cols[i - 1].tip ?? "" : ""}${i && cols[i - 1].tip ? " · " : ""}Sort by ${h}`}
                            sx={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", fontFamily: T.font, fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: sort.key === h ? T.ink2 : T.muted, textAlign: i ? "right" : "left", "&:hover": { color: T.ink2 } }}
                        >
                            {h}
                            {sort.key === h ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                        </Typography>
                    ))}
                </Box>
                {rows.map((r) => (
                    <Box
                        key={r.email}
                        onClick={onOpen ? () => onOpen(r) : undefined}
                        title={onOpen ? `Open ${r.name}` : undefined}
                        sx={{ ...rowSx, borderTop: `1px solid ${T.borderSoft}`, ...(onOpen ? { cursor: "pointer", "&:hover": { background: T.rowTint } } : {}) }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                            <Box sx={{ width: "28px", height: "28px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                                {initials(r.name)}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</Typography>
                                <Typography title={r.empty ? "No visits, leave, sickness or admin in this range" : undefined} sx={{ fontSize: "10.5px", color: r.empty ? T.amber : T.muted }}>{r.empty ? "⚠ No data" : r.team}</Typography>
                            </Box>
                        </Box>
                        {cols.map((c) => (
                            <Typography key={c.label} sx={{ ...numSx(c.strong), ...(c.colour?.(r) ? { color: c.colour(r), fontWeight: 700 } : {}) }}>{c.get(r)}</Typography>
                        ))}
                    </Box>
                ))}
                <Box sx={{ ...rowSx, borderTop: `2px solid ${T.border}` }}>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: T.ink2 }}>{totalLabel}</Typography>
                    {cols.map((c) => (
                        <Typography key={c.label} sx={{ ...numSx(c.strong), fontWeight: 800 }}>{c.total ? c.total(all, total) : ""}</Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const csvFor = (rows: Row[], cols: Col[], range: ReportRange) => {
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [
        [`# Tandem performance ${range.from} to ${range.to}.`],
        ["Consultant", "Team", "Weekdays", ...cols.map((c) => c.label)],
        ...rows.map((r) => [r.name, r.team, r.b.weekdays, ...cols.map((c) => c.get(r))]),
    ];
    return lines.map((l) => l.map(esc).join(",")).join("\r\n");
};

const PerformanceTab = ({ range, team = "AllTeams", consultant = "AllConsultants", onOpen }: { range: ReportRange; team?: string; consultant?: string; onOpen?: (email: string, name: string, teamName: string) => void }) => {
    const open = onOpen ? (r: Row) => onOpen(r.email, r.name, r.team) : undefined;
    const { config, showPoints } = usePointsConfig();
    const [showAllRows, setShowAllRows] = useState(false);
    const [sort, setSort] = useState<SortState>({ key: "Consultant", dir: 1 });
    const { from, to } = range;

    const roster = useActiveConsultants();
    const teamOf = useTeamOf();
    const inTeam = useTeamFilter(team);
    const visits = useVisitsByConsultant(from, to);
    const diary = useDiary(from, to);

    const days = useMemo(() => weekdaysBetween(from, to), [from, to]);
    const perWeekday = days.length / WEEKDAYS_PER_YEAR;
    const pointsGoal = round1(config.monthlyPointsTarget * 12 * perWeekday);

    const rows: Row[] = useMemo(() => {
        const inRange = new Set(days);
        return roster.active
            .filter((c) => inTeam(c.email))
            .filter((c) => consultant === "AllConsultants" || (c.email || "").toLowerCase() === consultant.toLowerCase())
            .map((c) => {
                const email = (c.email || "").toLowerCase();
                const done = (visits.byEmail.get(email) ?? []).filter(
                    (v: SfVisit) => v.visitStartDate && BOOKED_STATUSES.has(v.status || "") && inRange.has((v.visitStartDate as string).slice(0, 10)),
                );
                const byDay = new Map<string, SfVisit[]>();
                for (const v of done) {
                    const d = (v.visitStartDate as string).slice(0, 10);
                    byDay.set(d, [...(byDay.get(d) ?? []), v]);
                }
                const own = diary.byEmail.get(email);
                const b = breakdownFor(days, new Set(byDay.keys()), own);
                const monthly = monthlyVisitTarget(c.position) ?? fieldDaysPerMonth(config);
                return {
                    email,
                    name: c.name,
                    team: teamOf(email),
                    b,
                    visits: done.length,
                    visitsGoal: round1(monthly * 12 * perWeekday),
                    physical: done.filter((v) => v.visitCategory === "On Site").length,
                    remote: done.filter((v) => v.visitCategory === "Remote").length,
                    empty: byDay.size === 0 && !own?.size,
                    unflagged: done.filter((v) => v.visitCategory !== "On Site" && v.visitCategory !== "Remote").length,
                    missedDoubles: [...byDay.entries()].filter(([d, dv]) => !own?.has(d) && roomForSecond(dv)).length,
                    points: scoreConsultantPeriod(done, config).points,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [roster.active, visits.byEmail, diary.byEmail, days, teamOf, inTeam, consultant, config, perWeekday]);

    const cols = COLUMNS.filter((c) => showPoints || !c.points);
    const sortCol = cols.find((c) => c.label === sort.key);
    const sorted = [...rows].sort((a, b) => {
        const x = sortValue(a, sortCol);
        const y = sortValue(b, sortCol);
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir || a.name.localeCompare(b.name);
    });
    // Numbers sort highest first on the first click; names A to Z.
    const onSort = (key: string) => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === "Consultant" ? 1 : -1 }));
    const counted = rows;
    const total = counted.reduce((acc, r) => addB(acc, r.b), EMPTY);
    const perHead = (n: number) => (counted.length ? (n / counted.length).toFixed(1) : "—");
    const u = utilisation(total);
    const reached = counted.filter((r) => (showPoints ? r.points >= pointsGoal : r.visits >= r.visitsGoal)).length;
    const nothing = counted.filter((r) => r.visits === 0).length;

    const loading = roster.isLoading || visits.isLoading || diary.isLoading;
    if (loading)
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: T.muted, padding: "24px 0" }}>
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: "12.5px" }}>Loading {days.length} weekdays of visits and diary…</Typography>
            </Box>
        );
    if (visits.isError || diary.isError) return <Typography sx={{ fontSize: "12.5px", color: T.red }}>Couldn't load visits or diary for this range.</Typography>;

    const rank = (r: Row) => (showPoints ? r.points : r.visits);
    // One ranking for both lists; the worst list reads from the bottom up, so #1 is the single worst.
    const ranked = [...counted].sort((a, b) => rank(b) - rank(a) || a.name.localeCompare(b.name));
    const top = ranked.slice(0, 5);
    const bottom = ranked.slice(-5).reverse();
    const barMax = Math.max(1, ...top.map(rank), ...bottom.map(rank), showPoints ? pointsGoal : 0);
    const medal = ["🥇", "🥈", "🥉"];

    const performerRow = (r: Row, i: number, label: string, labelLarge: boolean) => {
        const goal = showPoints ? pointsGoal : r.visitsGoal;
        const value = rank(r);
        const colour = value >= goal ? T.green : value >= goal * 0.7 ? T.purpleDeep : T.amber;
        return (
            <Box
                key={r.email}
                onClick={open ? () => open(r) : undefined}
                title={open ? `Open ${r.name}` : undefined}
                sx={{ display: "grid", gridTemplateColumns: "30px minmax(0, 1.3fr) minmax(0, 1fr) 118px", gap: "10px", alignItems: "center", padding: "8px 0", borderTop: i === 0 ? "none" : `1px solid ${T.borderSoft}`, ...(open ? { cursor: "pointer", "&:hover": { background: T.rowTint } } : {}) }}
            >
                <Typography sx={{ fontSize: labelLarge ? "16px" : "12px", fontWeight: 800, color: T.muted, textAlign: "center" }}>{label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                    <Box sx={{ width: "26px", height: "26px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>{initials(r.name)}</Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</Typography>
                        <Typography sx={{ fontSize: "10.5px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.team}</Typography>
                    </Box>
                </Box>
                <Box sx={{ position: "relative", height: "12px", borderRadius: "4px", background: T.rowTint, border: `1px solid ${T.borderSoft}` }}>
                    <Box sx={{ position: "absolute", top: "1px", bottom: "1px", left: 0, width: `${(value / barMax) * 100}%`, background: colour, borderRadius: "3px", transition: "width .3s" }} />
                    <Box title={`target ${goal}`} sx={{ position: "absolute", top: "-2px", bottom: "-2px", width: "2px", background: T.ink2, left: `${Math.min(100, (goal / barMax) * 100)}%` }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 800, color: colour, fontVariantNumeric: "tabular-nums" }}>
                        {showPoints ? `${fmtPoints(value)} pts` : `${value} visits`} {value >= goal ? "✓" : ""}
                    </Typography>
                    <Typography sx={{ fontSize: "10.5px", color: T.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {r.physical} phys · {r.remote} rem · {utilisation(r.b).all}
                    </Typography>
                </Box>
            </Box>
        );
    };
    const none = <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing in this range.</Typography>;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Panel
                title="Team performance"
                sub={`${counted.length} consultants · ${days.length} weekdays`}
            >
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
                    <Figure value={u.all} label="Utilisation · all" color={T.purple} tip="(Booked + admin) ÷ available days" />
                    <Figure value={u.exAdmin} label="Utilisation · excl. admin" tip="Booked ÷ (available − admin)" />
                    <Figure value={u.visits} label="Visit utilisation" tip="Booked ÷ available" />
                    <Figure value={u.ratio} label="Booked to admin ratio" tip="Visit days for every admin day — higher means less time lost to admin" />
                    <Figure value={`${reached}/${counted.length}`} label={showPoints ? `Reached ${pointsGoal} points` : "Reached their visit target"} color={reached ? T.green : undefined} tip="Targets are set per month and pro-rated to this range" />
                    <Figure value={String(nothing)} label="Nothing recorded" color={nothing ? T.red : undefined} tip="No visits in the range — check before reading anything into it" />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "10px", mt: "10px" }}>
                    <Figure value={perHead(total.available)} label="Available / person" tip="Weekdays less leave and sickness" />
                    <Figure value={perHead(total.booked)} label="Booked / person" tip="Days with a Booked or Completed visit" />
                    <Figure value={perHead(total.admin)} label="Admin / person" tip="From Outlook calendar entries categorised Admin" />
                    <Figure value={perHead(total.gap)} label="Gap / person" tip="Available, with nothing booked" />
                    <Figure value={perHead(total.leave)} label="Leave / person" tip="Includes bank holidays" />
                    <Figure value={perHead(total.sickness)} label="Sick / person" tip="Sickness days" />
                </Box>
            </Panel>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                <Panel title="Top 5 performers" sub={showPoints ? `highest points in the range · goal ${pointsGoal}` : "most visits in the range · marker = the target for their grade"}>
                    {top.length ? top.map((r, i) => performerRow(r, i, medal[i] ?? `#${i + 1}`, i < 3)) : none}
                </Panel>
                <Panel title="Worst 5 performers" sub={showPoints ? `lowest points in the range, worst first · goal ${pointsGoal}` : "fewest visits in the range, worst first · marker = the target for their grade"}>
                    {bottom.length ? bottom.map((r, i) => performerRow(r, i, `#${i + 1}`, false)) : none}
                </Panel>
            </Box>

            <Panel
                title="By consultant"
                sub={`${rows.length} consultants · hover a column for how it's worked out`}
                action={
                    <Box
                        component="button"
                        onClick={() => {
                            const blob = new Blob([csvFor(sorted, cols, range)], { type: "text/csv;charset=utf-8" });
                            const a = document.createElement("a");
                            a.href = URL.createObjectURL(blob);
                            a.download = `tandem-performance-${from}-to-${to}.csv`;
                            a.click();
                            URL.revokeObjectURL(a.href);
                        }}
                        sx={{ border: "none", background: T.ink, color: "#fff", borderRadius: "8px", padding: "8px 14px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", fontFamily: T.font, "&:hover": { background: T.indigoDeep } }}
                    >
                        ⬇ Export CSV
                    </Box>
                }
            >
                <ConsultantTable
                    rows={showAllRows ? sorted : sorted.slice(0, 10)}
                    sort={sort}
                    onSort={onSort}
                    all={counted}
                    total={total}
                    cols={cols}
                    onOpen={open}
                    totalLabel={`Total · ${counted.length} consultants`}
                />
                {rows.length > 10 && <ShowAllButton expanded={showAllRows} total={rows.length} onToggle={() => setShowAllRows((v) => !v)} />}
            </Panel>
        </Box>
    );
};

export default PerformanceTab;
